import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, CheckSquare, Download, Search, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, Button, inputCls, Spinner } from "./ui-kit";

// Archivio ricette DF (backend: routers_recipe_library.py). Stessa struttura
// di "Apri archivio materie prime" (Ingredienti.jsx): si sceglie con un check
// e le ricette vengono copiate nel profilo dell'azienda, in bozza e senza
// quantità, da adattare e approvare.
const ALL = "Tutte le attività";
const searchable = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export default function RecipeLibraryModal({ onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [sector, setSector] = useState(ALL);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/app/catalog/recipes/library")
      .then(({ data }) => { setItems(data.recipes || []); setSectors(data.sectors || []); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => items.filter((item) =>
    (sector === ALL || item.sector === sector) &&
    (!search || searchable(item.name).includes(searchable(search)) || item.ingredient_names.some((n) => searchable(n).includes(searchable(search))))
  ), [items, sector, search]);
  // Tutte le attività: gruppi per attività; una sola attività: gruppi per categoria (Antipasti, Primi…).
  const groups = useMemo(() => {
    const map = new Map();
    visible.forEach((item) => {
      const key = sector === ALL ? item.sector : (item.category || "Altro");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return [...map.entries()];
  }, [visible, sector]);
  const selectableVisible = visible.filter((item) => !item.exists);
  const allVisibleSelected = selectableVisible.length > 0 && selectableVisible.every((item) => selected.includes(item.id));

  const toggleItem = (id) => setSelected((current) => current.includes(id) ? current.filter((itemId) => itemId !== id) : [...current, id]);
  const toggleGroup = (groupItems) => {
    const ids = groupItems.filter((item) => !item.exists).map((item) => item.id);
    const allSelected = ids.length > 0 && ids.every((id) => selected.includes(id));
    setSelected((current) => allSelected ? current.filter((id) => !ids.includes(id)) : Array.from(new Set([...current, ...ids])));
  };

  const importSelected = async () => {
    if (!selected.length) { toast.error("Seleziona almeno una ricetta dall'archivio"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/app/catalog/recipes/library/import", { library_ids: selected });
      const parts = [`${data.created} ${data.created === 1 ? "ricetta caricata" : "ricette caricate"} in bozza`];
      if (data.ingredients_created) parts.push(`${data.ingredients_created} materie prime aggiunte`);
      if (data.skipped) parts.push(`${data.skipped} già presenti`);
      toast.success(parts.join(" · "));
      (data.errors || []).forEach((err) => toast.error(`${err.name}: ${formatApiErrorDetail(err.detail)}`));
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end p-0 sm:place-items-start sm:p-6" data-testid="recipe-library-modal">
      <div className="absolute inset-0 bg-navy/60" onClick={onClose} />
      <Card className="relative flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-b-none p-0 sm:max-h-[94vh] sm:max-w-5xl sm:rounded-xl">
        <div className="border-b border-hairline bg-gradient-to-r from-blue-50 to-emerald-50 p-4 sm:p-5">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-blue-600 text-white"><BookOpen className="h-5 w-5" /></div>
              <div className="min-w-0">
                <h3 className="font-heading text-lg font-semibold text-navy sm:text-xl">Archivio ricette DF HACCP</h3>
                <p className="mt-1 text-sm text-slate-600">Scegli le ricette per tipologia di attività: entrano in bozza, con i soli ingredienti (senza quantità), da adattare e approvare.</p>
              </div>
            </div>
            <button onClick={onClose} className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-white" aria-label="Chiudi archivio"><X className="h-5 w-5" /></button>
          </div>
          <div className="relative mt-4">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ricetta o ingrediente…" className={`${inputCls} bg-white pl-10`} autoFocus data-testid="recipe-library-search" />
          </div>
          <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0" role="group" aria-label="Filtra per tipologia di attività">
            {[ALL, ...sectors].map((name) => (
              <button key={name} type="button" onClick={() => setSector(name)} aria-pressed={sector === name}
                className={`min-h-[36px] shrink-0 whitespace-nowrap rounded-full border px-3 text-sm font-medium transition-colors ${sector === name ? "border-blue-600 bg-blue-600 text-white" : "border-hairline bg-white text-slate-700 hover:border-blue-300"}`}
                data-testid={`recipe-library-sector-${name}`}>{name}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-b border-hairline bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="text-sm text-slate-600"><strong className="text-navy">{selected.length}</strong> selezionate · {visible.length} visualizzate</div>
          <button type="button" onClick={() => toggleGroup(visible)} disabled={!selectableVisible.length}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-blue-200 px-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50 sm:w-auto"
            data-testid="recipe-library-select-visible">
            <CheckSquare className="h-4 w-4" /> {allVisibleSelected ? "Deseleziona le visualizzate" : "Seleziona tutte le visualizzate"}
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-3 sm:p-5">
          {loading ? <div className="grid min-h-64 place-items-center"><Spinner /></div> : groups.map(([groupName, groupItems]) => {
            const selectable = groupItems.filter((item) => !item.exists);
            const allSelected = selectable.length > 0 && selectable.every((item) => selected.includes(item.id));
            return (
              <section key={groupName}>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-heading text-base font-semibold text-navy">{groupName} <span className="font-body text-sm font-normal text-slate-500">({groupItems.length})</span></h4>
                  <button type="button" onClick={() => toggleGroup(groupItems)} disabled={!selectable.length}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-blue-200 px-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                    data-testid={`recipe-library-select-group-${groupName}`}>
                    <CheckSquare className="h-4 w-4" /> {allSelected ? "Deseleziona tutte" : "Seleziona tutte"}
                  </button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {groupItems.map((item) => (
                    <label key={item.id} className={`flex min-h-[72px] items-start gap-3 rounded-xl border p-3 transition-colors ${item.exists ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-65" : selected.includes(item.id) ? "cursor-pointer border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300" : "cursor-pointer border-hairline bg-white hover:border-blue-300"}`} data-testid={`recipe-library-item-${item.id}`}>
                      <input type="checkbox" disabled={item.exists} checked={item.exists || selected.includes(item.id)} onChange={() => toggleItem(item.id)} className="mt-0.5 h-5 w-5 shrink-0 accent-emerald-600" />
                      <span className="min-w-0">
                        <span className="block font-medium text-navy">{item.name}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
                          {item.category}
                          {item.verified && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 font-semibold normal-case tracking-normal text-emerald-800">Verificata DF</span>}
                          {item.source === "clienti" && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 font-semibold normal-case tracking-normal text-amber-800">Da clienti DF</span>}
                        </span>
                        <span className="mt-1 block text-xs text-slate-600">{item.ingredient_names.join(", ")}</span>
                        {item.exists && <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Già presente</span>}
                        {!item.exists && item.allergen_labels?.length > 0 && <span className="mt-1 block text-xs text-red-600">Allergeni: {item.allergen_labels.join(", ")}</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </section>
            );
          })}
          {!loading && visible.length === 0 && <div className="grid min-h-64 place-items-center text-center text-sm text-slate-500">Nessuna ricetta corrisponde alla ricerca.</div>}
        </div>

        <div className="border-t border-hairline bg-white p-4">
          <p className="mb-3 text-xs text-slate-500">Shelf life e conservazione sono valori prudenziali predefiniti, non limiti di legge: verificali e adattali alla realtà dell’azienda prima di approvare. Le materie prime mancanti vengono aggiunte all’anagrafica con gli allergeni dell’archivio.</p>
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button variant="success" disabled={!selected.length || saving} onClick={importSelected} data-testid="recipe-library-import">
              <Download className="h-4 w-4" /> {saving ? "Caricamento in corso…" : `Carica ${selected.length || ""} ${selected.length === 1 ? "ricetta" : "ricette"} nel profilo`}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// "Salva nell'archivio DF" da una ricetta cliente (solo consulente, anche
// "entrato come cliente"): nell'archivio finiscono nome, categoria,
// ingredienti con allergeni, procedimento e conservazione, mai i dati
// dell'azienda, le quantità o i costi.
export const LIBRARY_SECTORS = ["Ristorante", "Pizzeria", "Supermercato / banco gastronomia", "Macelleria", "Pescheria", "Panificio", "Pasticceria", "Produttore di gastronomia", "Congelati/surgelati per il B2B"];

export function SaveToLibraryDialog({ recipe, onClose }) {
  const [sector, setSector] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!sector) { toast.error("Scegli la tipologia di attività"); return; }
    setSaving(true);
    try {
      await api.post(`/app/catalog/recipes/${recipe.id}/save-to-library`, { sector });
      toast.success(`«${recipe.name}» salvata nell’archivio ricette DF`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-end p-0 sm:place-items-center sm:p-6" data-testid="save-to-library-dialog">
      <div className="absolute inset-0 bg-navy/60" onClick={onClose} />
      <Card className="relative w-full rounded-b-none p-5 sm:max-w-md sm:rounded-xl">
        <h3 className="font-heading text-lg font-semibold text-navy">Salva nell’archivio ricette DF</h3>
        <p className="mt-1 text-sm text-slate-600"><strong>{recipe.name}</strong> sarà disponibile per tutti i clienti. Vengono salvati ingredienti, allergeni, procedimento e conservazione; mai i dati dell’azienda, le quantità o i costi.</p>
        <label className="mt-4 block text-sm font-medium text-navy" htmlFor="save-library-sector">Tipologia di attività</label>
        <select id="save-library-sector" value={sector} onChange={(e) => setSector(e.target.value)} className={`${inputCls} mt-1`} data-testid="save-to-library-sector">
          <option value="">Scegli…</option>
          {LIBRARY_SECTORS.map((name) => <option key={name}>{name}</option>)}
        </select>
        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button variant="success" disabled={!sector || saving} onClick={save} data-testid="save-to-library-confirm"><BookOpen className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva nell’archivio"}</Button>
        </div>
      </Card>
    </div>
  );
}
